import Member from 'iam/models/Member'
import { CancellationToken } from 'infra/cancellation'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { Observable, Subject, combineLatest, firstValueFrom } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  switchMap,
  takeUntil,
} from 'rxjs/operators'
import { CursorPageQuery, CursorPaged, Id } from 'types/definitions'
import { fromStream, toStream } from 'utils/mobx-util'
import { shareLatest } from 'utils/rx-util'
import { DraftStateSlice, PrepareContext } from './DraftStateSlice'
import { StepFactory, StepViewModel } from './OutlineViewModel'
import { IamPort } from './ports'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import {
  RequestedProviderChanged,
  RequestedProviderSelection,
} from './DraftChange'
import { SearchTextViewModel } from 'infra/SearchTextViewModel'

const TITLE = 'Assign a provider to the job'
const DESCRIPTION = "The Pro won't have access to the job until it's submitted."

/**
 * Request Provider Selector view model, for requesting the job's champion provider.
 */
export default class RequestProviderSelectorViewModel extends DraftStateSlice {
  /**
   * The singleton step for this slice.
   */
  readonly step: StepViewModel

  /**
   * Changes subject.
   * @private
   */
  private readonly changesSubject = new Subject<RequestedProviderChanged>()

  /**
   * The search text.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Changes stream.
   */
  readonly changes$ = this.changesSubject.asObservable()

  /**
   * The requested provider.
   */
  @observable _requestedProvider: RequestedProviderSelection | null = null

  /**
   * Emits the requested provider on subscribe, but only after `prepare` has been run.
   */
  readonly requestedProvider$ = toStream(
    () => this.requestedProvider,
    true,
  ).pipe(distinctUntilChanged(), shareLatest(), this.whileReady())

  /**
   * Streams as MobX Observables.
   */
  private readonly $ = {
    selectedWorkspaceMember: fromStream(null, this.selectedWorkspaceMember$),
    selectedOwner: fromStream(null, this.selectedOwner$),
  }

  /**
   * Constructor.
   *
   * @param isActive$ Observable for whether the slice is active.
   * @param createStep Factory for creating a Step.
   * @param selectedOwner$ Observable for the selected job owner
   * @param selectedWorkspaceMember$ Observable for the selected workspace member
   * @param adapter The IAM adapter.
   * @param _isDisabled Getter for whether the selector is disabled
   * @param _canEditDraftJobsAsAdmin Getter for whether the selector is visible
   */
  constructor(
    isActive$: Observable<boolean>,
    createStep: StepFactory,
    private selectedWorkspaceMember$: Observable<Member | null>,
    private selectedOwner$: Observable<Member | null>,
    private adapter: IamPort,
    private _isDisabled: () => boolean,
    private _canEditDraftJobsAsAdmin: () => boolean,
  ) {
    super(isActive$)
    makeObservable(this)
    this.requestedProvider$.subscribe()
    this.changes$.subscribe()

    combineLatest([
      toStream(() => this.scrollProvider, true),
      toStream(() => this.requestedProvider, true),
    ])
      .pipe(
        filter(([, requestedProvider]) => requestedProvider === null),
        switchMap(([scrollProvider]) => scrollProvider.fetch()),
        this.whileReady(),
      )
      .subscribe()

    this.searchText.debouncedValue$
      .pipe(this.whileReady())
      .subscribe(() => this.scrollProvider.fetch())

    this.step = createStep({
      stepId: 'request_provider_selection',
      isValid: () => Boolean(this._requestedProvider),
      outlineItemDisplay: () => {
        if (
          !this.requestedProvider ||
          this.requestedProvider === RequestedProviderSelection.firstAvailable
        ) {
          return {
            title: TITLE,
            description: DESCRIPTION,
            value: this.nullSelectionPlaceholder,
            visual: {
              type: 'EmptyAvatar',
            },
          }
        } else {
          return {
            title: TITLE,
            description: DESCRIPTION,
            value: this.requestedProvider.displayName,
            visual: {
              type: 'Image',
              url: this.requestedProvider.avatar,
            },
          }
        }
      },
    })
  }

  @computed
  get nullSelectionPlaceholder() {
    return 'First available'
  }

  /**
   * This is the only step container.
   */
  @computed
  get stepContainers() {
    const member = this.$.selectedWorkspaceMember()

    if (
      member?.hasPermission('ADMIN_USER_LIST') &&
      member?.hasPermission('ADMIN_JOB_CREATE') &&
      this._canEditDraftJobsAsAdmin()
    ) {
      return [this]
    }

    return []
  }

  /**
   * Whether the request provider selector is disabled.
   */
  @computed
  get disabled() {
    return this._isDisabled()
  }

  /**
   * The scroll provider for Members.
   */
  @computed
  get scrollProvider() {
    return new InfiniteScrollProvider<RequestedProviderSelection>({
      limit: 20,
      fetchItems: async (params) => this.fetchMembers(params),
      startFetchTaskAsPending: true,
    })
  }

  /**
   * The currently requested provider.
   */
  @computed
  get requestedProvider() {
    if (!this._requestedProvider) {
      return null
    }

    if (
      this._requestedProvider === RequestedProviderSelection.firstAvailable ||
      this.availableMembers.includes(this._requestedProvider)
    ) {
      return this._requestedProvider
    }

    return null
  }

  /**
   * Includes the null selector.
   */
  @computed
  get filteredMembers(): RequestedProviderSelection[] {
    const owner = this.$.selectedOwner()
    const searchText = this.searchText.value.toLowerCase()

    const availableMembers = this.availableMembers.filter(
      (m) => m.user.id !== owner?.user.id,
    )

    if (!searchText) {
      return [RequestedProviderSelection.firstAvailable, ...availableMembers]
    }

    return availableMembers.filter((m) =>
      m.displayName.toLowerCase().includes(searchText),
    )
  }

  @computed
  get availableMembers() {
    const member = this.$.selectedWorkspaceMember()
    const workspace = member?.workspace
    if (!workspace || !member?.hasPermission('ADMIN_USER_LIST')) {
      return []
    }

    return this.adapter.availableMembersForWorkspace(workspace.id)
  }

  /**
   * Hydrates the Provider Selector state from the Job.
   *
   * @param context
   * @param ct
   */
  async prepare(context: PrepareContext, ct = CancellationToken.default) {
    // Wait for the WS selector to have been prepared.
    await firstValueFrom(
      this.selectedWorkspaceMember$.pipe(takeUntil(ct.asObservable())),
      { defaultValue: null },
    )

    ct.throwIfCancelled()

    if (context.type === 'NewDraft') {
      this.setRequestedPro(RequestedProviderSelection.firstAvailable)
    } else {
      this.setRequestedPro(
        context.job.requestedPro ?? RequestedProviderSelection.firstAvailable,
      )
    }
  }

  /**
   * Sets the requested provider.
   *
   * @param provider
   */
  @action.bound
  setRequestedPro(provider: RequestedProviderSelection | null) {
    this._requestedProvider = provider
  }

  /**
   * Requests the specified provider.
   *
   * @param provider
   */
  @action.bound
  selectProvider(provider: RequestedProviderSelection | undefined | null) {
    provider ??= null

    if (provider !== this._requestedProvider) {
      this.setRequestedPro(provider)

      this.changesSubject.next({
        requestedProvider:
          provider ?? RequestedProviderSelection.firstAvailable,
        type: 'RequestedProviderChanged',
      })
    }
  }

  /**
   * Triggers new member search.
   *
   * @param newText
   */
  @action.bound
  triggerSearch(newText: string) {
    this.searchText.change(newText)
  }

  /**
   * Fetches the workspace's members.
   */
  fetchMembers = task.resolved(async (params: CursorPageQuery) => {
    const member = this.$.selectedWorkspaceMember()

    if (!member?.hasPermission('ADMIN_USER_LIST')) {
      return CursorPaged.empty
    }

    return this.adapter.fetchAvailableMembersForWorkspace({
      ...params,
      workspace_id: Id.assert(member.workspace.id),
      search_text: this.searchText.value,
    })
  })
}
